import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Dropdown from "./Dropdown";
import Tag from "./Tag";
import Button from "../ui/Button";
import styles from "./HubAvailableLanguagesPicker.module.css";

export interface HubLanguageOption {
    value: string;
    label: string;
    code: string;
}

export interface HubAvailableLanguagesPickerProps {
    selectedIds: string[];
    onChange: (ids: string[]) => void;
    options: HubLanguageOption[];
    /** Language option ids that cannot be removed (still used in hub content) */
    lockedIds?: string[];
    disabled?: boolean;
}

const HubAvailableLanguagesPicker: React.FC<HubAvailableLanguagesPickerProps> = ({
    selectedIds,
    onChange,
    options,
    lockedIds = [],
    disabled = false,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const locked = useMemo(() => new Set(lockedIds), [lockedIds]);
    const [pendingAddId, setPendingAddId] = useState("");

    const selectedOptions = useMemo(() => options.filter((o) => selectedIds.includes(o.value)), [options, selectedIds]);

    const addableOptions = useMemo(() => options.filter((o) => !selectedIds.includes(o.value)), [options, selectedIds]);

    const dropdownOptions = useMemo(
        () => addableOptions.map((o) => ({ value: o.value, label: o.label })),
        [addableOptions],
    );

    const confirmAndRemove = (opt: HubLanguageOption) => {
        if (locked.has(opt.value) || disabled) return;
        if (!window.confirm(t("hubLanguageRemoveConfirm", { name: opt.label }))) return;
        onChange(selectedIds.filter((x) => x !== opt.value));
    };

    const handleAdd = () => {
        if (!pendingAddId || selectedIds.includes(pendingAddId)) return;
        onChange([...selectedIds, pendingAddId]);
        setPendingAddId("");
    };

    const canAdd = pendingAddId && !selectedIds.includes(pendingAddId);
    const addDisabled = disabled || addableOptions.length === 0 || !canAdd;

    return (
        <div className={styles.picker}>
            <div className={styles.tags}>
                {selectedOptions.length === 0 ? (
                    <span className={styles.empty}>{t("hubLanguagesPickerEmpty")}</span>
                ) : (
                    selectedOptions.map((opt) => {
                        const isLocked = locked.has(opt.value);
                        if (isLocked) {
                            return (
                                <span key={opt.value} title={t("hubLanguageLockedInStoriesTooltip")}>
                                    <Tag
                                        text={opt.label}
                                        className="tag--locked"
                                        backgroundColor="var(--surface-2)"
                                        color="var(--ink-2)"
                                    />
                                </span>
                            );
                        }
                        return (
                            <Tag
                                key={opt.value}
                                text={opt.label}
                                onRemove={disabled ? undefined : () => confirmAndRemove(opt)}
                            />
                        );
                    })
                )}
            </div>

            <div className={styles.addRow}>
                <Dropdown
                    label=""
                    options={dropdownOptions}
                    value={pendingAddId}
                    onChange={(v) => setPendingAddId(v || "")}
                    placeholder={t("hubLanguagesPickerAddPlaceholder")}
                    disabled={disabled || addableOptions.length === 0}
                    loading={false}
                    error={false}
                    isWithoutMargin
                />
                <Button
                    label={t("hubLanguagesAddButton")}
                    className={`btn-outline ${styles.addBtn}`}
                    onClick={handleAdd}
                    disabled={addDisabled}
                />
            </div>
        </div>
    );
};

export default HubAvailableLanguagesPicker;
